import { ChatMessage, Organization, OrganizationMember } from '@prisma/client'
import { useEffect, useMemo, useRef, useState } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { Api } from '~/core/trpc'
import { ChatSocketPayload } from '~/features/chatV3/common'
import { SocketClient } from '~/plugins/socket/client'
import { Toolbox } from '~/plugins/toolbox/shared'

type OnNewMessage = (payload: ChatSocketPayload) => void
type OnUnknownConversation = (payload: ChatSocketPayload) => void

type Props = {
  conversationIds: string[]
  organization: Organization
  organizationMemberAgent?: OrganizationMemberFromContext
  organizationMembers: OrganizationMemberFromContext[]
  onNewMessage?: OnNewMessage
  onUnknownConversation?: OnUnknownConversation
}

const TIMEOUT_MS = 5000
const TIMEOUT_OPTIMISTIC_MS = 15 * 1000

export const useParticipantsTyping = ({
  conversationIds = [],
  organization,
  organizationMemberAgent,
  organizationMembers,
  onNewMessage,
  onUnknownConversation,
}: Props) => {
  const refOrganizationMemberAgent = useRef<OrganizationMemberFromContext>(
    organizationMemberAgent,
  )

  const refOnNewMessage = useRef<OnNewMessage>(onNewMessage)

  const refConversationIds = useRef<string[]>([])

  const refTimeoutByHash = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  )

  const [payloads, setPayloads] = useState<Record<string, ChatSocketPayload>>(
    {},
  )

  const api = Api.useUtils()

  const checkStatus = async () => {
    const canRun = organizationMemberAgent && conversationIds.length === 1

    if (!canRun) {
      return
    }

    try {
      const response = await api.chatCustom.findConversationStatus.fetch({
        conversationId: conversationIds[0],
      })

      if (response.status === 'running') {
        handleSocket(response.payload)
      }
    } catch (error) {
      // ignore
    }
  }

  useEffect(() => {
    refConversationIds.current = conversationIds
  }, [conversationIds])

  useEffect(() => {
    checkStatus()

    refOrganizationMemberAgent.current = organizationMemberAgent
  }, [organizationMemberAgent])

  const reset = (hash: string) => {
    setPayloads(map => {
      const mapUpdated = { ...map }
      delete mapUpdated[hash]
      return mapUpdated
    })

    const timeout = refTimeoutByHash.current.get(hash)

    if (timeout) {
      clearTimeout(timeout)
      refTimeoutByHash.current.delete(hash)
    }
  }

  const store = (
    hash: string,
    payload: ChatSocketPayload,
    timeout = TIMEOUT_MS,
  ) => {
    if (!refTimeoutByHash.current) {
      return
    }

    setPayloads(map => {
      const mapUpdated = { ...map }
      mapUpdated[hash] = payload
      return mapUpdated
    })

    const timeoutBefore = refTimeoutByHash.current.get(hash)

    if (timeoutBefore) {
      clearTimeout(timeoutBefore)
    }

    const timeoutRefreshed = setTimeout(() => {
      reset(hash)
    }, timeout)

    refTimeoutByHash.current.set(hash, timeoutRefreshed)
  }

  const handleSocket = (_payload: ChatSocketPayload, _timeout?: number) => {
    const payload = { ..._payload }
    const organizationMemberAgentId = payload.organizationMemberId
    const organizationMemberAgentIdSet = refOrganizationMemberAgent?.current?.id

    let timeout = _timeout
    let conversationId = payload.conversationId

    const canUseConversationId =
      !conversationId &&
      conversationIds.length === 1 &&
      organizationMemberAgentIdSet &&
      organizationMemberAgentIdSet === organizationMemberAgentId

    if (canUseConversationId) {
      conversationId = conversationIds[0]

      payload.conversationId = conversationId

      if (Toolbox.Utility.isNull(timeout)) {
        timeout = TIMEOUT_OPTIMISTIC_MS
      }
    }

    const isConversationWatched =
      refConversationIds?.current.includes(conversationId)

    if (!isConversationWatched) {
      if (onUnknownConversation) {
        onUnknownConversation(payload)
      }

      return
    }

    if (
      payload.type !== 'typing' &&
      payload.type !== 'typing-stop' &&
      payload.type !== 'new-message' &&
      payload.type !== 'new-reaction'
    ) {
      return
    }

    const hash = [conversationId + organizationMemberAgentId].join('')

    const canReset =
      payload.type === 'new-reaction' ||
      payload.type === 'new-message' ||
      payload.type === 'typing-stop'

    if (canReset) {
      reset(hash)
    } else {
      store(hash, payload, timeout)
    }

    if (refOnNewMessage.current && payload.type === 'new-message') {
      refOnNewMessage.current(payload)
    }
  }

  SocketClient.useEvent<ChatSocketPayload>(
    `organization-${organization.id}-chat`,
    payload => {
      handleSocket(payload)
    },
  )

  const onTypeOptimistic = (
    chatMessage: ChatMessage,
    organizationMemberAgent: OrganizationMember,
  ) => {
    return handleSocket(
      {
        type: 'typing',
        organizationMemberId: organizationMemberAgent.id,
        organizationId: organization.id,
        conversationId: chatMessage.conversationId,
        messageId: chatMessage.id,
        parentId: chatMessage.parentId,
        typing: { content: 'is thinking' },
      },
      TIMEOUT_OPTIMISTIC_MS,
    )
  }

  const typing = useMemo(() => {
    const items: Record<
      string,
      {
        organizationMember: OrganizationMemberFromContext
        content: string
        parentId?: string
      }
    > = {}

    for (const payload of Object.values(payloads)) {
      const organizationMember = organizationMembers.find(
        candidate => candidate.id === payload.organizationMemberId,
      )

      if (organizationMember) {
        items[payload.conversationId] = {
          organizationMember,
          content: payload.typing?.content,
          parentId: payload.parentId,
        }
      }
    }

    return items
  }, [payloads])

  return {
    typing,
    onTypeOptimistic,
  }
}

export type TypingType = ReturnType<typeof useParticipantsTyping>['typing']
